import { dispatchCustomEvent } from "./Utilities";

/**
 * Service worker updates
 * After a deploy, the browser downloads the new service worker in the background, but it waits
 * to take over until every tab of the site is closed - an installed app that's never fully
 * closed can stay on the old version for a long time. This notices the waiting update so the app
 * can offer it (UpdateNotice.js), and switches over when the user says so.
 */

export const UPDATE_AVAILABLE_EVENT = "lpb-update-available";

// How often an open page asks the server for a newer version - this app is often kept open for
// hours (or installed and run for days), so it can't rely on the next page load.
const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000;

let waitingWorker = null;

// The update waiting to take over, if one has downloaded. A getter as well as the event, since
// the update can be found before UpdateNotice is listening.
export function getWaitingWorker() {
  return waitingWorker;
}

function announceUpdate(worker) {
  waitingWorker = worker;
  dispatchCustomEvent(UPDATE_AVAILABLE_EVENT, null);
}

// Watches one registration for an update finishing its download. Only counts when a service
// worker is already in control - the very first install isn't an "update".
function watchForUpdates(registration) {
  if (registration.waiting && navigator.serviceWorker.controller) {
    announceUpdate(registration.waiting);
  }
  registration.addEventListener("updatefound", () => {
    const installingWorker = registration.installing;
    if (!installingWorker) return;
    installingWorker.addEventListener("statechange", () => {
      if (installingWorker.state === "installed" && navigator.serviceWorker.controller) {
        announceUpdate(installingWorker);
      }
    });
  });

  setInterval(() => registration.update().catch(() => {}), UPDATE_CHECK_INTERVAL_MS);
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "visible") registration.update().catch(() => {});
  });
}

export function registerServiceWorker() {
  // Production only - a service worker on localhost would keep serving stale development files.
  if (!("serviceWorker" in navigator) || window.location.hostname === "localhost") return;
  window.addEventListener("load", () => {
    navigator.serviceWorker
      .register("./service-worker.js")
      .then(watchForUpdates)
      .catch((error) => console.error("Service worker registration failed:", error));
  });
}

// Tells the waiting update to take over now, then reloads once it has, so the page runs the new
// version's files. Game progress is saved as it happens, so the caller picks up where it left off.
export function applyUpdate() {
  if (!waitingWorker) {
    window.location.reload();
    return;
  }
  navigator.serviceWorker.addEventListener("controllerchange", () => window.location.reload(), { once: true });
  waitingWorker.postMessage({ type: "SKIP_WAITING" });
}
